import React, { useContext, useEffect } from "react";
import { CategoryTitle, Loader, LoadingIndicator } from "elements";
import { NavLink, useHistory, useParams } from "polyrouter";
import { useTranslation } from "utilities/i18n";
import { Layout } from "views/Main/Layouts";
import { Card, makeColumnContentForCard } from "collections/Cards";
import { Section } from "collections/Section";
import { AuditEvent, useGetAuditEvent } from "../useAuditEvents";
import { toLicenseDateLocaleDateString } from "utilities/licenseServerUtils";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";
import { handleNullFields } from "utilities/stringUtils";

const AuditEventInformation: React.FC<{
  auditEvent: AuditEvent;
}> = ({ auditEvent }) => {
  const { t } = useTranslation();

  const fields = [
    {
      key: "eventType",
      label: t("EVENT_TYPE"),
      content: <p>{handleNullFields(auditEvent.eventTypeName)}</p>
    },
    {
      key: "creationTime",
      label: t("CREATION_TIME"),
      content: <p>{toLicenseDateLocaleDateString(auditEvent.eventTime)}</p>
    },
    {
      key: "userId",
      label: t("USER_ID"),
      content: <p>{handleNullFields(auditEvent.userId)}</p>
    },
    {
      key: "userName",
      label: t("USER_NAME"),
      content: <p>{handleNullFields(auditEvent.userName)}</p>
    },
    {
      key: "tenantId",
      label: t("TENANT_ID"),
      content: <p>{handleNullFields(auditEvent.tenantId)}</p>
    },
    {
      key: "entitlementName",
      label: t("ENTITLEMENT_NAME"),
      content: <p>{handleNullFields(auditEvent.entitlementName)}</p>
    },
    {
      key: "instanceName",
      label: t("PRODUCT_NAME"),
      content: <p>{handleNullFields(auditEvent.productName)}</p>
    }
  ];

  const baseTextAreaClass = "form-control code no-resize";

  const valueFields = [
    {
      key: "previousValue",
      label: t("PREVIOUS_VALUE"),
      content: (
        <div className="pr-1">
          <textarea
            className={`${baseTextAreaClass} rows-8`}
            readOnly={true}
            value={auditEvent.previousValue}
          />
        </div>
      )
    },
    {
      key: "newValue",
      label: t("NEW_VALUE"),
      content: (
        <div className="pr-2">
          <textarea
            className={`${baseTextAreaClass} rows-8`}
            readOnly={true}
            value={auditEvent.newValue}
          />
        </div>
      )
    }
  ];

  const additionInfoFields = [
    {
      key: "additionalInfo",
      label: t("ADDITIONAL_INFO"),
      content: (
        <div className="pr-2">
          <textarea
            className={`${baseTextAreaClass} rows-2`}
            readOnly={true}
            value={auditEvent.additionalInfo}
          />
        </div>
      )
    }
  ];

  return (
    <>
      <Section>
        <Section.Header>{t("AUDIT_EVENT_INFORMATION")}</Section.Header>
        <Card className="table-card full-width">
          {makeColumnContentForCard(fields, 4)}
          {makeColumnContentForCard(valueFields, 2)}
          {makeColumnContentForCard(additionInfoFields, 1, "pb-2")}
        </Card>
      </Section>
    </>
  );
};

export const AuditEventDetails: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";
  const history = useHistory();
  const { Content, Sidebar } = Layout;

  const { data, fetching } = useGetAuditEvent({
    variables: { tenantId: tenantId, recordId: id }
  });

  useEffect(() => {
    if (!fetching && tenantId) {
      if (!data || tenantId !== data.tenantId) {
        history.push("/manage/licensing/clariti/audit-events");
      }
    }
  }, [fetching, data, history, tenantId, id]);

  if (fetching) {
    return <LoadingIndicator />;
  }

  return (
    <>
      <Sidebar>
        <span className="title-capitalized">{t("CLARITI")}</span>
        <ul className="menu router">
          <li>
            <NavLink nest exact to={""}>
              {t("OVERVIEW")}
            </NavLink>
          </li>
        </ul>
      </Sidebar>
      <Content className="audit-event-details">
        <Loader loading={fetching}>
          {data && (
            <>
              <Content.Title>
                <CategoryTitle
                  title={t("OVERVIEW")}
                  subtitle={t("AUDIT_EVENT_DETAILS")}
                />
              </Content.Title>
              <AuditEventInformation auditEvent={data} />
            </>
          )}
        </Loader>
      </Content>
    </>
  );
};
